/**
 * Login page with GitHub and Entra ID OAuth
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Github } from 'lucide-react';
import { Button } from '@/components/ui';
import {
  initiateGitHubLogin,
  isPublicGitHubEnabled,
  initiateEntraIdLogin,
  isEntraIdEnabled,
} from '@/services/authService';
import { useAuth } from '@/contexts/AuthContext';

export function LoginPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  // Redirect to dashboard if already logged in
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  const handleLogin = () => {
    initiateGitHubLogin('github');
  };

  const handlePublicGitHubLogin = () => {
    initiateGitHubLogin('publicgithub');
  };

  const handleEntraIdLogin = () => {
    initiateEntraIdLogin();
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">ADC Portal</h1>
          <p className="text-gray-400">Agent Developer Compute Management Console</p>
        </div>

        <div className="bg-gray-800 rounded-lg shadow-xl p-8 border border-gray-700">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-white mb-2">Welcome</h2>
            <p className="text-gray-400">Sign in to continue</p>
          </div>

          <div className="flex flex-col gap-3">
            {isEntraIdEnabled() && (
              <Button
                onClick={handleEntraIdLogin}
                variant="primary"
                size="lg"
                className="w-full flex items-center justify-center gap-3"
              >
                <svg width="20" height="20" viewBox="0 0 23 23" fill="currentColor">
                  <path d="M0 0h10.931v10.931H0zM12.069 0H23v10.931H12.069zM0 12.069h10.931V23H0zM12.069 12.069H23V23H12.069z" />
                </svg>
                Sign in with Microsoft
              </Button>
            )}

            <Button
              onClick={handleLogin}
              variant="secondary"
              size="lg"
              className="w-full flex items-center justify-center gap-3"
            >
              <Github size={20} />
              Sign in with GitHub EMU
            </Button>

            {isPublicGitHubEnabled() && (
              <Button
                onClick={handlePublicGitHubLogin}
                variant="secondary"
                size="lg"
                className="w-full flex items-center justify-center gap-3"
              >
                <Github size={20} />
                Sign in with GitHub
              </Button>
            )}
          </div>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-400">
              By signing in, you agree to access ADC resources through your GitHub account
            </p>
          </div>
        </div>

        <div className="mt-8 text-center text-sm text-gray-400">
          <p>Need help? Contact your administrator</p>
        </div>
      </div>
    </div>
  );
}
